import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import moment from "moment";
import { listApps } from "../../services/integration.service";
import { ToastrContext } from "../../provider/toastrContext";
import { WorkspaceContext } from "../../provider/workspaceContext";
import searchingImg from "../../assets/img/illustrations/searching.png";
import CreateAppModal from "./component/createAppModal";

const PAGE_LIMIT = 20;

const Developers = () => {
  const { workspace } = useContext(WorkspaceContext);
  const { showError } = useContext(ToastrContext);
  const [apps, setApps] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = async (after = null) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_LIMIT };
      if (after) params.starting_after = after;
      const res = await listApps(params);
      const items = Array.isArray(res.data) ? res.data : [];
      setApps((prev) => (after ? [...prev, ...items] : items));
      setHasMore(!!res.has_more);
      setCursor(res.next_cursor || null);
    } catch (e) {
      if (e && e.status === 403) {
        showError(
          "This workspace is no longer accessible. Please switch workspaces."
        );
      } else {
        showError((e && e.message) || "Failed to load apps");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setApps([]);
    setCursor(null);
    setHasMore(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace && workspace.idt_user_workspace]);

  const handleCreated = () => {
    setApps([]);
    setCursor(null);
    setHasMore(false);
    load();
  };

  return (
    <div className="container-fluid p-0">
      <Helmet>
        <title>Developers — Otokata</title>
      </Helmet>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="float-start">
                <h5 className="card-title mt-2 mb-0">Developers</h5>
                <p className="text-muted mb-0">
                  API keys, webhooks, and events for your workspace integrations
                </p>
              </div>
              <div className="float-end">
                <Button
                  variant="primary"
                  onClick={() => setShowModal(true)}
                >
                  <FiPlus className="align-middle me-1" size={16} />
                  Create App
                </Button>
              </div>
            </div>
            <div className="card-body table-responsive">
              {loading && apps.length === 0 ? (
                <div className="text-center p-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : apps.length === 0 ? (
                <div className="text-center p-5">
                  <img
                    src={searchingImg}
                    alt="No apps yet"
                    style={{ maxWidth: 200 }}
                  />
                  <h6 className="mt-3">No apps yet</h6>
                  <p className="text-muted">
                    Create your first app to mint API keys and receive webhook
                    events.
                  </p>
                </div>
              ) : (
                <>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>API Version</th>
                        <th>Last Used</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apps.map((app) => (
                        <tr key={app.id}>
                          <td>{app.name}</td>
                          <td>
                            <span
                              className={`badge bg-${
                                app.status === "active"
                                  ? "success"
                                  : "secondary"
                              }`}
                            >
                              {app.status}
                            </span>
                          </td>
                          <td>{app.type || "—"}</td>
                          <td>{app.api_version || "—"}</td>
                          <td>
                            {app.ts_last_used
                              ? moment(app.ts_last_used).format(
                                  "DD-MM-YYYY HH:mm"
                                )
                              : "—"}
                          </td>
                          <td>
                            {app.created
                              ? moment.unix(app.created).format("DD-MM-YYYY")
                              : "—"}
                          </td>
                          <td>
                            <Link
                              to={`/developers/${app.public_app_id}`}
                              className="btn btn-sm btn-primary"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {hasMore && (
                    <div className="text-center">
                      <Button
                        variant="outline-secondary"
                        onClick={() => load(cursor)}
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Load more"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <CreateAppModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default Developers;
